import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { formatApiError } from "../lib/errors";
import { Sparkles, AlertTriangle } from "lucide-react";
import RudrasLogo from "../components/RudrasLogo";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";

import ChatHeader from "../components/chat/ChatHeader";
import ChatSidebar from "../components/chat/ChatSidebar";
import ChatMessages from "../components/chat/ChatMessages";
import ChatInput from "../components/chat/ChatInput";
import ChatOnboarding from "../components/chat/ChatOnboarding";
import HealthcareHero from "../components/chat/HealthcareHero";
import HealthcareSidebar from "../components/chat/HealthcareSidebar";
import "../components/chat/ChatLayout.css";

/**
 * Claude v2.4-inspired public chatbot shell.
 *
 * ⚠ ALL backend calls + lead capture + transcript + voice + session logic
 *    are unchanged from the previous implementation — only the presentation
 *    layer is new. This keeps tenant isolation, embeddings retrieval,
 *    SendGrid email pipeline, and the demo flow intact.
 */

// Default fallback pills shown only if the backend hasn't supplied tailored
// suggestions for some reason. Real prompts come from
// `meta.suggested_prompts` (industry / KB / session-aware) and refresh from
// each `/chat` response.
const DEFAULT_PILLS = [
  "What do you offer?",
  "Where are you located?",
  "How can I reach you?",
  "How much does it cost?",
];

export default function PublicChatbot() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  // Embed mode: chrome-free version for use inside an iframe widget.
  const isEmbed = searchParams.get("embed") === "1";

  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [ttsOn, setTtsOn] = useState(false);
  // Consultative Experience v1 — Brief / Elaborate response mode. Per-session
  // persistence via localStorage so a page refresh remembers the user's
  // preference. Defaults to "brief" (chat-like feel).
  const [responseMode, setResponseModeState] = useState(() => {
    try {
      const v = localStorage.getItem("rch:responseMode");
      return v === "elaborate" ? "elaborate" : "brief";
    } catch (_e) { return "brief"; }
  });
  const setResponseMode = (m) => {
    const next = m === "elaborate" ? "elaborate" : "brief";
    setResponseModeState(next);
    try { localStorage.setItem("rch:responseMode", next); } catch (_e) { /* ignore */ }
  };
  // Premium Voice Layer v1 — speaking + interruption state.
  const [speaking, setSpeaking] = useState(false);
  const [ttsProvider, setTtsProvider] = useState("openai");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcriptEmail, setTranscriptEmail] = useState("");
  const [lead, setLead] = useState({ name: "", email: "", phone: "", inquiry_type: "general", message: "", consent: true });
  // Dynamic suggested prompts — initial set comes from meta, refreshed via each chat response.
  const [pills, setPills] = useState(DEFAULT_PILLS);
  // Step 4 / Part A — isProcessing guard. Ref (not state) so toggling it
  // doesn't trigger re-renders during the response/TTS/modal lifecycle.
  // `uiBusy` mirrors the ref's value as state ONLY for the input/button
  // disabled visual — never read for control flow.
  const isProcessing = useRef(false);
  const isProcessingTimerRef = useRef(null);
  const [uiBusy, setUiBusy] = useState(false);
  // Step 2 — Modal System (tenant-configurable structured intake forms).
  // `pendingModal` queues a modal that should open AFTER Sara finishes speaking.
  // `activeModal`  is the currently rendered modal (with field answers + submit state).
  const [pendingModal, setPendingModal] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [modalAnswers, setModalAnswers] = useState({});
  const [modalSubmitting, setModalSubmitting] = useState(false);
  // Experience Layer v1 — pre-chat onboarding state.
  // `onboardingDone` gates the chat shell when the tenant requires an
  // acknowledgement. `visitorContext` is forwarded with every /chat call
  // so the AI can naturally reference the visitor's name/company.
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [visitorContext, setVisitorContext] = useState({});
  const recogRef = useRef(null);
  // Premium Voice Layer v1 — refs for the currently playing audio + abort hook.
  const audioRef = useRef(null);
  const ttsAbortRef = useRef(null);

  useEffect(() => {
    api.get(`/public/chatbot/${slug}`)
      .then(r => {
        setMeta(r.data);
        setMessages([{ role: "assistant", content: r.data.chatbot.welcome_message }]);
        if (Array.isArray(r.data.suggested_prompts) && r.data.suggested_prompts.length) {
          setPills(r.data.suggested_prompts);
        }
        // Experience Layer v1 — restore any prior onboarding completion
        // from this tab's sessionStorage so a refresh doesn't re-prompt
        // the visitor mid-conversation.
        try {
          const raw = sessionStorage.getItem(`rch:onboarding:${slug}`);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.completed) {
              setOnboardingDone(true);
              if (parsed?.visitor && typeof parsed.visitor === "object") {
                setVisitorContext(parsed.visitor);
              }
            }
          }
          // If onboarding is disabled by tenant config, treat as complete.
          if (r.data?.chatbot?.onboarding_config?.enabled === false) {
            setOnboardingDone(true);
          }
        } catch (_) { /* no-op */ }
      })
      .catch(e => {
        // P0 — backend may return `detail` as a structured object
        // (e.g. {code, message, provisioning_status} on 403). Coerce to a
        // safe string before storing in state so React never tries to
        // render an object child (React error #31 / blank page).
        const status = e?.response?.status;
        const fallback =
          status === 403
            ? "This assistant isn't available right now."
            : status === 404
            ? "Chatbot not found"
            : status === 410
            ? "This demo has expired."
            : "Assistant unavailable";
        setError(formatApiError(e, fallback));
      });
  }, [slug]);

  // Best-effort session close on tab unload — gives the backend a final
  // chance to persist a deterministic session summary + escalation snapshot.
  // Uses sendBeacon so it survives the unload race.
  useEffect(() => {
    const handler = () => {
      if (!sessionId) return;
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/chatbot/${slug}/session/close`;
        const payload = JSON.stringify({ session_id: sessionId, slug, reason: "tab_close" });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
        }
      } catch (_) { /* swallow — best-effort */ }
    };
    window.addEventListener("pagehide", handler);
    return () => window.removeEventListener("pagehide", handler);
  }, [sessionId, slug]);

  // Premium Voice Layer v1 — abort any in-flight TTS playback when the
  // component unmounts so we don't leak audio sessions.
  useEffect(() => () => interruptSpeech("unmount"), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 4 / Part A — single source of truth for the busy guard.
  // beginProcessing: stamp the ref, mirror to state for the disabled-input
  // visual, and arm a 30-second safety timeout. endProcessing() is the only
  // sanctioned way to clear it.
  const beginProcessing = (label = "") => {
    if (isProcessing.current) return false; // already busy — caller should bail
    isProcessing.current = true;
    setUiBusy(true);
    if (isProcessingTimerRef.current) clearTimeout(isProcessingTimerRef.current);
    isProcessingTimerRef.current = setTimeout(() => {
      // Safety net — network stall, audio failure, etc. Always restores UX.
      // Intentional console.warn per spec.
      // eslint-disable-next-line no-console
      console.warn(`[isProcessing] safety timeout fired (label=${label}) — auto-resetting after 30s`);
      isProcessing.current = false;
      setUiBusy(false);
      isProcessingTimerRef.current = null;
    }, 30000);
    return true;
  };
  const endProcessing = () => {
    if (isProcessingTimerRef.current) {
      clearTimeout(isProcessingTimerRef.current);
      isProcessingTimerRef.current = null;
    }
    isProcessing.current = false;
    setUiBusy(false);
  };

  // Step 4 / Part A — automatic release. End the busy state when ALL of these
  // are true:
  //   - We're not still loading the LLM response (`sending === false`)
  //   - TTS has finished speaking (or is disabled / never started)
  //   - Any queued modal has been opened (or there was none)
  // Modal SUBMIT is intentionally out of this guard (Part A spec).
  useEffect(() => {
    if (!isProcessing.current) return;
    if (sending) return;
    if (speaking) return;
    if (pendingModal) return; // wait until the modal actually opens
    endProcessing();
  }, [sending, speaking, pendingModal, activeModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 2 — Modal System. Open the queued modal as soon as it's safe:
  //  - If voice is on, wait for `speaking` to flip back to false (audio ended).
  //  - If voice is off / TTS never started, the effect still resolves
  //    immediately because `speaking` is already false.
  // We use a tiny grace period (120 ms) so the modal doesn't visually
  // hit the same render frame as the last spoken sentence — feels less abrupt.
  useEffect(() => {
    if (!pendingModal) return;
    if (speaking) return; // wait — `setSpeaking(false)` will re-run this effect
    const t = setTimeout(() => {
      setActiveModal(pendingModal);
      // Seed default answers (empty strings) so controlled inputs stay sane.
      const seed = {};
      (pendingModal.fields || []).forEach((f) => { seed[f.id] = ""; });
      setModalAnswers(seed);
      setPendingModal(null);
    }, 120);
    return () => clearTimeout(t);
  }, [pendingModal, speaking]);

  // Step 2 — Submit the active modal's answers. The endpoint validates the
  // field set against the tenant's modal definition and writes a structured
  // intake row onto the lead doc (creates a partial lead if needed).
  const submitActiveModal = async (e) => {
    e?.preventDefault?.();
    if (!activeModal || modalSubmitting) return;
    // Client-side required-field check (server re-validates).
    const missing = (activeModal.fields || []).filter(
      (f) => f.required && !String(modalAnswers[f.id] || "").trim()
    );
    if (missing.length > 0) {
      toast.error(`Please fill: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    setModalSubmitting(true);
    try {
      await api.post(`/public/chatbot/${slug}/intake`, {
        modal_id: activeModal.id,
        session_id: sessionId,
        answers: modalAnswers,
      });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Thanks! I'll have those details ready for " +
            (meta?.chatbot?.team_contact_name || meta?.company?.name || "our team")
            + ".",
        },
      ]);
      setActiveModal(null);
      setModalAnswers({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — please try again.");
    } finally {
      setModalSubmitting(false);
    }
  };

  const dismissActiveModal = () => {
    setActiveModal(null);
    setModalAnswers({});
  };

  // ---- Premium Voice Layer v1 ----
  // Reports an interrupt event to the backend so telemetry can roll forward.
  const reportVoiceEvent = (event) => {
    if (!sessionId) return;
    try {
      api.post(`/public/chatbot/${slug}/voice/event`, { session_id: sessionId, event }).catch(() => {});
    } catch (_) { /* swallow — telemetry is best-effort */ }
  };

  // Stop ANY in-flight playback / pending TTS request. Used when the visitor
  // hits the mic mid-speech ("interruption") or when the page unloads.
  const interruptSpeech = (reason = "user_interrupt") => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
    } catch (_) {}
    try {
      if (ttsAbortRef.current) ttsAbortRef.current.abort();
      ttsAbortRef.current = null;
    } catch (_) {}
    try { window.speechSynthesis?.cancel?.(); } catch (_) {}
    if (speaking) {
      setSpeaking(false);
      if (reason === "user_interrupt") reportVoiceEvent("interruption");
    }
  };

  // Speak the assistant's reply. Tries the server-side premium TTS first;
  // falls back transparently to browser Web Speech on any failure so the
  // conversation never breaks. Returns immediately if voice is muted.
  const speak = async (text, opts = {}) => {
    if (!ttsOn || !text) return;
    // If we're already speaking, cancel the previous play before starting.
    interruptSpeech("new_reply");

    const voiceEnabled = meta?.voice_settings?.voice_enabled;
    if (!voiceEnabled) {
      // Tenant has voice off — straight to browser TTS.
      return speakBrowser(text);
    }

    setSpeaking(true);
    const controller = new AbortController();
    ttsAbortRef.current = controller;
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/chatbot/${slug}/voice/tts`;
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          text,
          session_id: sessionId,
          emotion: opts.emotion || null,
        }),
      });
      if (!resp.ok) {
        // 502 → fallback signal from server. 404/400 → also fall back gracefully.
        console.warn("[voice.tts] server returned", resp.status, "— falling back to browser");
        setSpeaking(false);
        return speakBrowser(text);
      }
      // The endpoint MAY return JSON {fallback:true,...} when voice is disabled
      // or the provider isn't wired. Detect via content-type.
      const ct = resp.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        const json = await resp.json();
        if (json?.fallback) {
          setSpeaking(false);
          return speakBrowser(text);
        }
      }
      // Respect the server's recommended pre-reply delay (humanly believable).
      const minDelayMs = Number(resp.headers.get("x-voice-min-delay-ms")) || 0;
      setTtsProvider(resp.headers.get("x-voice-provider") || "openai");
      const blob = await resp.blob();
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      audio.preload = "auto";
      audioRef.current = audio;
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(audioUrl); };
      audio.onerror = () => { setSpeaking(false); URL.revokeObjectURL(audioUrl); speakBrowser(text); };
      // Human pacing — let the visitor breathe before we start talking.
      const startAt = Math.max(0, minDelayMs);
      setTimeout(() => {
        // If the visitor interrupted between the fetch + start, bail.
        if (audioRef.current !== audio) return;
        audio.play().catch((e) => {
          console.warn("[voice.tts] play failed", e);
          setSpeaking(false);
          // P0 Demo Regression fix — autoplay-blocked or audio sink error.
          if (e?.name === "NotAllowedError" || e?.name === "AbortError") {
            toast.message("Click to enable audio playback.");
          }
          speakBrowser(text);
        });
      }, startAt);
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.warn("[voice.tts] error — falling back to browser", e);
      }
      setSpeaking(false);
      if (e?.name !== "AbortError") speakBrowser(text);
    } finally {
      ttsAbortRef.current = null;
    }
  };

  // Browser Web Speech fallback — kept exactly as the legacy implementation.
  const speakBrowser = (text) => {
    if (!window.speechSynthesis) {
      // P0 Demo Regression fix — provider AND browser fallback both unavailable.
      toast.error("Voice is temporarily unavailable. Text chat is still available.");
      return;
    }
    setTtsProvider("browser");
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.0;
      setSpeaking(true);
      u.onend = () => setSpeaking(false);
      u.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
    } catch (e) {
      setSpeaking(false);
      toast.error("Voice is temporarily unavailable. Text chat is still available.");
    }
  };

  const send = async (textArg, opts = {}) => {
    const msg = (textArg ?? input).trim();
    if (!msg || sending) return;
    // P0 Demo Regression fix — disclaimer gate. Never send a chat request
    // until the visitor has acknowledged the inline onboarding card. This
    // is the single source of truth for the gate — pills, sidebar quick
    // questions, hero suggestions, and voice input all funnel through here.
    if (!isEmbed && Boolean(meta?.chatbot?.onboarding_config?.enabled) && !onboardingDone) {
      toast.message("Please accept the disclaimer to start the chat.");
      return;
    }
    // Step 4 / Part A — isProcessing guard. Bail if we're already mid-cycle.
    // beginProcessing arms a 30-second safety timeout that will reset the UI
    // if the response/TTS/modal lifecycle never fully resolves.
    if (!beginProcessing("send")) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post(`/public/chatbot/${slug}/chat`, {
        session_id: sessionId,
        message: msg,
        voice_mode: Boolean(opts.voice_mode),
        // Consultative Experience v1 — response-mode hint (Brief / Elaborate).
        response_mode: responseMode || "brief",
        // Experience Layer v1 — forward any visitor metadata collected at
        // onboarding. The backend persists this on the conversation doc
        // (first-write-wins) and injects it into the system prompt so the
        // AI can naturally reference name / company.
        visitor_context: visitorContext || {},
      });
      setSessionId(data.session_id);
      // Step 2 — Modal System. Detect tenant-configured trigger tokens in the
      // raw response, strip them BEFORE displaying or speaking, and queue the
      // matching modal for after-speech rendering. Restaurant + other tenants
      // with no modals configured fall through unchanged.
      const configuredModals = meta?.chatbot?.modals || [];
      let displayText = data.response || "";
      let triggeredModal = null;
      if (configuredModals.length > 0 && displayText) {
        for (const m of configuredModals) {
          const tok = m?.trigger_token;
          if (!tok) continue;
          if (displayText.indexOf(tok) !== -1) {
            triggeredModal = m;
            // Remove EVERY occurrence so the user never sees the token.
            // Allow optional surrounding whitespace / leading newline.
            const escaped = tok.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            displayText = displayText.replace(new RegExp(`\\s*${escaped}\\s*`, "g"), " ").trim();
            break; // one modal per response is enough
          }
        }
      }
      setMessages(m => [...m, { role: "assistant", content: displayText }]);
      if (triggeredModal) setPendingModal(triggeredModal);
      if (Array.isArray(data.suggested_prompts) && data.suggested_prompts.length) {
        setPills(data.suggested_prompts);
      }
      // Premium Voice Layer v1 — forward the detected visitor emotion so the
      // TTS layer can apply subtle pacing / pitch adjustments.
      const emotion = data?.sales_intelligence?.sales_brain?.emotion_detected || null;
      speak(displayText, { emotion });
    } catch (e) {
      toast.error("Send failed. Please try again.");
      // Step 4 / Part A — explicit release on network error so the UI
      // recovers immediately instead of waiting for the 30s safety timeout.
      endProcessing();
    } finally { setSending(false); }
  };

  const toggleVoice = () => {
    // P0 Demo Regression fix — disclaimer gate also covers voice input.
    if (!isEmbed && Boolean(meta?.chatbot?.onboarding_config?.enabled) && !onboardingDone) {
      toast.message("Please accept the disclaimer to start the chat.");
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input not supported in this browser"); return; }
    if (listening) { recogRef.current?.stop(); return; }
    // P0 Demo Regression fix — when the visitor explicitly engages voice
    // mode, also enable spoken replies so Sara is audible. The visitor can
    // still mute via the speaker icon in the header. We only auto-enable
    // on the first activation (not while looping mic on/off).
    if (!ttsOn) {
      setTtsOn(true);
      toast.message("Voice mode on — Sara will reply out loud.");
    }
    // Premium Voice Layer v1 — if the assistant is currently speaking and the
    // visitor opens the mic, treat that as an interruption: cancel playback
    // immediately so they aren't talked over.
    if (speaking) interruptSpeech("user_interrupt");

    // Phase 1 — pull pacing config from the meta endpoint (with sensible
    // defaults). Tolerate low-confidence audio + short pauses inside an
    // utterance; do NOT treat keyboard / breathing as completion.
    const cfg = meta?.voice_config || {};
    const silenceMs = cfg.silence_threshold_ms ?? 1800;
    const minConf = cfg.min_confidence ?? 0.55;
    const minChars = cfg.min_utterance_chars ?? 3;
    const maxMs = cfg.max_utterance_ms ?? 25000;

    const r = new SR();
    r.lang = "en-US";
    r.continuous = true;        // Keep the mic open across short pauses.
    r.interimResults = true;    // Lets us debounce on silence.

    let fullTranscript = "";
    let silenceTimer = null;
    let hardTimer = null;
    let lastFinalisedAt = 0;

    const stopAndSend = () => {
      try { r.stop(); } catch (_) { /* ignore */ }
      const text = fullTranscript.trim();
      if (text.length < minChars) {
        // Treat as a false-trigger — visitor opened mic but nothing real arrived.
        reportVoiceEvent("false_trigger");
        return;
      }
      // Debounce — avoid a double-send race.
      const now = Date.now();
      if (now - lastFinalisedAt < (cfg.debounce_ms ?? 500)) return;
      lastFinalisedAt = now;
      setInput(text);
      send(text, { voice_mode: true });
    };

    const resetSilence = () => {
      if (silenceTimer) clearTimeout(silenceTimer);
      silenceTimer = setTimeout(stopAndSend, silenceMs);
    };

    r.onresult = (e) => {
      // Walk the latest results, filtering out low-confidence noise.
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        const alt = res[0];
        if (!alt) continue;
        if (typeof alt.confidence === "number" && alt.confidence > 0 && alt.confidence < minConf) {
          // Treat as noise — don't extend the transcript.
          continue;
        }
        if (res.isFinal) {
          fullTranscript += (fullTranscript ? " " : "") + alt.transcript.trim();
        }
      }
      resetSilence();
    };

    r.onstart = () => {
      setListening(true);
      fullTranscript = "";
      resetSilence();
      hardTimer = setTimeout(stopAndSend, maxMs);
    };
    r.onend = () => {
      setListening(false);
      if (silenceTimer) clearTimeout(silenceTimer);
      if (hardTimer) clearTimeout(hardTimer);
    };
    r.onerror = (ev) => {
      setListening(false);
      if (silenceTimer) clearTimeout(silenceTimer);
      if (hardTimer) clearTimeout(hardTimer);
      // 'no-speech' / 'aborted' are not real errors — silently ignore.
      const benign = new Set(["no-speech", "aborted"]);
      if (!benign.has(ev.error)) {
        console.warn("[voice]", ev.error);
      }
    };

    recogRef.current = r;
    try { r.start(); } catch (_) { /* already started */ }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/public/chatbot/${slug}/lead`, {
        ...lead,
        session_id: sessionId || "no-session"
      });
      console.log("[lead] saved", data);
      toast.success("Thanks! We'll be in touch shortly.");
      setShowLead(false);
      setMessages(m => [...m, { role: "assistant", content: `Thanks ${lead.name}! Someone from ${meta.company.name} will reach out shortly.` }]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join("; ")
                : typeof detail === "string" ? detail
                : "Failed to submit — please check your email and try again.";
      toast.error(msg);
      console.error("[lead] submit failed", err.response?.status, err.response?.data);
    }
  };

  const requestTranscript = async (e) => {
    e.preventDefault();
    if (!sessionId) { toast.error("Start a conversation first"); return; }
    try {
      const res = await api.post(`/public/chatbot/${slug}/transcript`, { session_id: sessionId, email: transcriptEmail });
      const { status, message } = res.data || {};
      const fallback = "Transcript request received.";
      if (status === "sent") {
        toast.success(message || "Transcript emailed successfully.");
      } else if (status === "not_configured") {
        toast.message(message || "Transcript request saved. Email delivery is not configured yet.", {
          description: "The business owner will receive a copy in their email logs.",
          duration: 6000,
        });
      } else if (status === "failed") {
        toast.error(message || "We couldn't send the transcript right now.");
      } else {
        toast.success(message || fallback);
      }
      setShowTranscript(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message;
      console.error("[transcript] failed", err?.response?.status, err?.response?.data);
      toast.error(typeof detail === "string" ? detail : "Failed to send transcript. Please try again.");
    }
  };

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center p-8 bg-gradient-to-br from-emerald-50 via-white to-amber-50/40" data-testid="chatbot-not-found">
      <Link to="/" className="inline-flex items-center gap-2.5 mb-10 group">
        <RudrasLogo size="xl" ringed className="ring-amber-300/30" />
        <span className="font-display font-semibold text-2xl tracking-tight text-emerald-950">Rudras-AI</span>
      </Link>
      <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-emerald-700/80 mb-3">Assistant unavailable</div>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mb-3 max-w-lg">{error}</h1>
      <p className="text-slate-600 max-w-md mb-8 leading-relaxed text-sm">
        This chatbot may have moved, been disabled by its owner, or the link is mistyped. Try the live demo, or head home.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-emerald-900 hover:bg-emerald-950 text-white text-sm font-medium shadow-md transition" data-testid="chatbot-error-home">
          Back to home
        </Link>
        <Link to="/chat/rudras-healthcare-demo" className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-white border border-emerald-200 hover:border-emerald-400 text-emerald-900 text-sm font-medium transition" data-testid="chatbot-error-demo">
          Try the live demo
        </Link>
      </div>
    </div>
  );
  if (!meta) return (
    <div className="min-h-screen grid place-items-center"><div className="animate-pulse-soft text-muted-foreground">Loading…</div></div>
  );

  const tone = meta.chatbot.theme_color || "#5B4DFF";
  // Convert hex to lighter shade for the gradient end-point — keep things readable
  // without doing fancy color math: just use the same color for both stops if
  // the tenant set a custom one; otherwise stick with Claude's twin gradient.
  const tone2 = meta.chatbot.theme_color ? meta.chatbot.theme_color : "#7B6FFF";

  // Healthcare-vertical presentation switch. Industry-driven, NOT slug-driven —
  // every future healthcare tenant (industry="healthcare") inherits the
  // polished healthcare hero + sidebar automatically. All non-healthcare
  // tenants (restaurant, rudras-ai, demos, etc.) render the original
  // generic Claude-v2.4 shell, unchanged.
  const isHealthcare = (meta.company?.industry || "").toLowerCase() === "healthcare";

  // First-turn hero: only show pills when no user turns yet.
  const userTurns = messages.filter(m => m.role === "user").length;
  const showHero = userTurns === 0;
  // Experience Layer v2 — In-thread conversational onboarding. The chat
  // workspace (header + sidebar + input bar) stays mounted continuously;
  // only the "hero" slot inside the message-scroller is replaced with the
  // inline onboarding card until the visitor completes/acknowledges it.
  const onboardingConfig = meta.chatbot.onboarding_config;
  const showOnboarding = !isEmbed
    && Boolean(onboardingConfig?.enabled)
    && !onboardingDone;
  const onboardingHero = showOnboarding ? (
    <ChatOnboarding
      inline
      config={onboardingConfig}
      slug={slug}
      companyName={meta.company?.name}
      assistantName={meta.chatbot?.assistant_name}
      onComplete={(visitor) => {
        setVisitorContext(visitor || {});
        setOnboardingDone(true);
      }}
    />
  ) : null;
  const hero = onboardingHero ?? (showHero ? (
    isHealthcare ? (
      <HealthcareHero
        companyName={meta.company?.name}
        assistantName={meta.chatbot?.assistant_name}
        welcomeMessage={meta.chatbot?.welcome_message}
        consultativeIntro={meta.chatbot?.consultative_intro}
        credentialsStrip={meta.chatbot?.credentials_strip}
        suggestedPrompts={pills}
        onAsk={(q) => { if (!uiBusy) send(q); }}
        uiBusy={uiBusy}
      />
    ) : (
    <div className="rch-hero">
      <h2 className="rch-hero-h">
        Hi! I'm <span>{meta.chatbot.name || meta.company.name}'s assistant</span>.
      </h2>
      {/* Slice 1 (Feb 2026) — Sales AI Persona role caption pill. Only rendered
          when the backend surfaces a non-empty role_caption (industry-aware). */}
      {meta.chatbot?.assistant_meta?.role_caption && (
        <div
          className="rch-hero-role"
          data-testid="rch-assistant-role"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 12px",
            borderRadius: 999,
            background: "rgba(15, 23, 42, 0.06)",
            color: "#475569",
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: 0.3,
            marginTop: 8,
            marginBottom: 4,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: 999, background: "#10b981" }} />
          {meta.chatbot.assistant_meta.display_name}
          <span style={{ opacity: 0.6 }}>· {meta.chatbot.assistant_meta.role_caption}</span>
        </div>
      )}
      <p className="rch-hero-sub">Ask me anything — I'm trained on this business and reply in seconds.</p>
      {slug === "rudras-ai" && !isEmbed && (
        <Link to="/demo" className="rch-hero-build-banner" data-testid="rch-hero-build-banner">
          <Sparkles className="w-4 h-4" aria-hidden />
          <div className="rch-hero-build-text">
            <strong>See your own AI Agent</strong>
            <span> · trained on your website in ~30 seconds</span>
          </div>
          <span className="rch-hero-build-arrow" aria-hidden>→</span>
        </Link>
      )}
      <div className="rch-pills" data-testid="rch-suggested-pills">
        {pills.map(q => (
          <button
            key={q}
            className="rch-pill"
            disabled={uiBusy}
            // Step 4 / Part A — guard at the top of the handler. If we're
            // already mid-cycle, ignore the click (this is the "sidebar quick
            // button" equivalent — pills are our suggested-prompt buttons).
            onClick={() => { if (uiBusy) return; send(q); }}
            data-testid={`rch-pill-${q.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
            style={uiBusy ? { opacity: 0.6, pointerEvents: "none" } : undefined}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
    )
  ) : null);

  const demoBadge = (meta.chatbot.is_temporary && !isEmbed) ? (
    <p className="rch-disclaimer" data-testid="demo-footer-badge" style={{ marginTop: 8 }}>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: "rgba(91,77,255,0.1)", color: tone, border: "1px solid rgba(91,77,255,0.25)" }}>
        <Sparkles className="w-3 h-3"/> Powered by Rudras-AI
      </span>
      {meta.chatbot.expires_at && (
        <span style={{ marginLeft: 8 }}>· demo expires {new Date(meta.chatbot.expires_at).toLocaleDateString()}</span>
      )}
    </p>
  ) : null;

  // Inline per-tenant accent override (does NOT affect the rest of the app —
  // these variables are scoped to .rch-shell).
  const shellStyle = {
    ["--rch-accent"]: tone,
    ["--rch-accent-2"]: tone2,
  };

  // (Experience Layer v2) — onboarding now renders inline in the message
  // thread; the legacy fullscreen takeover branch has been removed.

  return (
    <div className={`rch-shell ${isEmbed ? "embed" : ""} ${isHealthcare ? "is-healthcare" : ""}`} style={shellStyle} data-testid="rch-shell">
      <ChatHeader
        name={meta.chatbot.name || meta.company.name}
        logoUrl={meta.chatbot.logo_url}
        ttsOn={ttsOn}
        onToggleTts={() => setTtsOn(t => !t)}
        onToggleSidebar={() => setSidebarOpen(o => !o)}
        onOpenLead={() => setShowLead(true)}
        onExit={() => {
          // Final Polish v1 — clean exit from a chat session. We prefer to
          // close the tab when it was opened from another page (so investor
          // walkthroughs feel "I'm done here, back to the dashboard"); fall
          // back to navigating to the marketing landing if the tab was
          // opened directly.
          try {
            if (window.opener && !window.opener.closed) { window.close(); return; }
          } catch (_e) { /* fallthrough */ }
          window.location.href = "/";
        }}
        isEmbed={isEmbed}
        isInternalRudras={slug === "rudras-ai"}
        variant={isHealthcare ? "healthcare" : ""}
        // Consultative Experience v1
        responseMode={responseMode}
        onSetResponseMode={isHealthcare ? setResponseMode : undefined}
        voiceEnabled={Boolean(meta.chatbot?.voice_enabled)}
        listening={listening}
        onToggleVoice={toggleVoice}
      />

      <div className="rch-body">
        {!isEmbed && (
          isHealthcare ? (
            <HealthcareSidebar
              open={sidebarOpen}
              company={meta.company}
              chatbot={meta.chatbot}
              featureFlags={meta.chatbot?.feature_flags}
              suggestedPrompts={pills}
              onAsk={(q) => send(q)}
              onOpenLead={() => setShowLead(true)}
              onClose={() => setSidebarOpen(false)}
            />
          ) : (
            <ChatSidebar
              open={sidebarOpen}
              company={meta.company}
              suggestedPrompts={pills}
              onAsk={(q) => send(q)}
              onOpenLead={() => setShowLead(true)}
              onClose={() => setSidebarOpen(false)}
            />
          )
        )}

        <div className="rch-chat">
          <ChatMessages
            messages={messages}
            sending={sending}
            logoUrl={meta.chatbot.logo_url}
            hero={hero}
          />
          {/* Premium Voice Layer v1 — minimal status bar visible only when voice is engaged. */}
          {(listening || speaking) && (
            <div className="rch-voice-status" data-testid="voice-status-bar" aria-live="polite">
              <div className={`rch-voice-wave ${speaking ? "is-speaking" : "is-listening"}`} aria-hidden="true">
                <span /><span /><span /><span /><span />
              </div>
              <div className="rch-voice-label">
                {listening ? (
                  <span data-testid="voice-listening-label">Listening…</span>
                ) : (
                  <span data-testid="voice-speaking-label">
                    {(meta?.chatbot?.assistant_name || "Assistant")} is speaking{ttsProvider === "browser" ? " · using your browser voice" : ""}
                    <button
                      className="rch-voice-interrupt"
                      onClick={() => interruptSpeech("user_interrupt")}
                      data-testid="voice-interrupt-btn"
                      type="button"
                    >Interrupt</button>
                  </span>
                )}
              </div>
            </div>
          )}
          <ChatInput
            value={input}
            onChange={setInput}
            onSend={() => send()}
            onToggleVoice={toggleVoice}
            listening={listening}
            // Step 4 / Part A — disable input + send button while the
            // response/TTS/modal cycle is busy. `uiBusy` covers cases the
            // existing `sending` flag misses (TTS still playing, modal
            // queued but not yet shown). Also disabled while in-thread
            // onboarding is incomplete (Experience Layer v2).
            sending={sending || uiBusy || showOnboarding}
            onOpenLead={() => setShowLead(true)}
            onOpenTranscript={() => setShowTranscript(true)}
            disclaimer={meta.chatbot.disclaimer}
            footerNote={meta.chatbot.footer_note}
            demoBadge={demoBadge}
            samplePrompts={(!showHero && pills.length > 0) ? pills.slice(0, 4) : []}
            onSamplePrompt={(q) => { if (!uiBusy) send(q); }}
          />
        </div>
      </div>

      <Dialog open={showLead} onOpenChange={setShowLead}>
        <DialogContent className="rch-modal" style={shellStyle}>
          <div className="rch-modal-header">
            <DialogTitle asChild>
              <h2 className="rch-modal-title">Leave your details</h2>
            </DialogTitle>
            <DialogDescription asChild>
              <p className="rch-modal-desc">We'll get back to you shortly — typically within one business day.</p>
            </DialogDescription>
          </div>
          <div className="rch-modal-body">
            <form onSubmit={submitLead} data-testid="lead-form">
              <input required placeholder="Name" value={lead.name} onChange={e => setLead({...lead, name: e.target.value})} data-testid="lead-name"/>
              <input required type="email" placeholder="Email" value={lead.email} onChange={e => setLead({...lead, email: e.target.value})} data-testid="lead-email"/>
              <input placeholder="Phone (optional)" value={lead.phone} onChange={e => setLead({...lead, phone: e.target.value})} data-testid="lead-phone"/>
              <select value={lead.inquiry_type} onChange={e => setLead({...lead, inquiry_type: e.target.value})} data-testid="lead-type">
                <option value="general">General inquiry</option>
                <option value="pricing">Pricing</option>
                <option value="catering">Catering</option>
                <option value="support">Support</option>
                <option value="product">Product</option>
                <option value="appointment">Appointment</option>
                <option value="other">Other</option>
              </select>
              <textarea rows={3} placeholder="Message (optional)" value={lead.message} onChange={e => setLead({...lead, message: e.target.value})}/>
              <label className="rch-modal-consent">
                <input type="checkbox" checked={lead.consent} onChange={e => setLead({...lead, consent: e.target.checked})} data-testid="lead-consent"/>
                <span>I agree my messages, contact details, IP and transcript may be shared with the business for follow-up.</span>
              </label>
              <button type="submit" className="rch-modal-submit" data-testid="lead-submit">Submit</button>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showTranscript} onOpenChange={setShowTranscript}>
        <DialogContent className="rch-modal" style={shellStyle}>
          <div className="rch-modal-header">
            <DialogTitle asChild>
              <h2 className="rch-modal-title">Email me the transcript</h2>
            </DialogTitle>
            <DialogDescription asChild>
              <p className="rch-modal-desc">We'll send the full conversation to your inbox.</p>
            </DialogDescription>
          </div>
          <div className="rch-modal-body">
            <form onSubmit={requestTranscript}>
              <input required type="email" placeholder="your@email.com" value={transcriptEmail} onChange={e => setTranscriptEmail(e.target.value)} data-testid="transcript-email"/>
              <button type="submit" className="rch-modal-submit" data-testid="transcript-submit">Send transcript</button>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Step 2 — Tenant-configurable Modal System (renders only when a
          trigger token has been detected AND any in-flight TTS has finished). */}
      {activeModal && (
        <div
          className="rch-intake-backdrop"
          data-testid={`modal-${activeModal.id}`}
          role="dialog"
          aria-modal="true"
        >
          <div className="rch-intake-card" style={{ maxWidth: 480 }}>
            <h2 className="rch-modal-title" data-testid={`modal-title-${activeModal.id}`}>
              {activeModal.title || "Tell us more"}
            </h2>
            <form onSubmit={submitActiveModal} className="rch-intake-form">
              {(activeModal.fields || []).map((f) => {
                const val = modalAnswers[f.id] ?? "";
                const common = {
                  id: `modal-${activeModal.id}-${f.id}`,
                  "data-testid": `modal-field-${f.id}`,
                  required: !!f.required,
                  placeholder: f.placeholder || "",
                  value: val,
                  onChange: (e) =>
                    setModalAnswers((a) => ({ ...a, [f.id]: e.target.value })),
                };
                return (
                  <label key={f.id} className="rch-intake-row">
                    <span className="rch-intake-label">
                      {f.label}{f.required ? " *" : ""}
                    </span>
                    {f.type === "select" ? (
                      <select {...common}>
                        <option value="" disabled>— Select —</option>
                        {(f.options || []).map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    ) : f.type === "textarea" ? (
                      <textarea {...common} rows={4} />
                    ) : (
                      <input
                        {...common}
                        type={
                          f.type === "tel" ? "tel" :
                          f.type === "email" ? "email" :
                          f.type === "number" ? "number" : "text"
                        }
                      />
                    )}
                  </label>
                );
              })}
              <div className="rch-intake-actions">
                <button
                  type="submit"
                  className="rch-modal-submit"
                  disabled={modalSubmitting}
                  data-testid={`modal-submit-${activeModal.id}`}
                >
                  {modalSubmitting ? "Sending…" : (activeModal.submit_label || "Submit")}
                </button>
                <button
                  type="button"
                  className="rch-intake-dismiss"
                  onClick={dismissActiveModal}
                  data-testid={`modal-dismiss-${activeModal.id}`}
                >
                  No thanks, I'll ask later
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
